import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type {
    ICampaign,
    ICampaignCommissionConfig,
} from '../../../redux/buzzbridge/types';
import { emptyCommissionConfig, sanitizeCommissionForPayload } from '../../../utils/commissionCampaign';
import { CommissionCampaignFields } from '../CommissionCampaignFields';
import { buzzBridgeConfigSelector } from '../../../selectors/selectors';
import { areAllPlatformAutoLinksEnabled, formatDisabledAutoLinkPlatforms } from '../../../utils/brandSocialAutoLink';
import { getUgcCampaignMaxSlots } from '../../../utils/ugcCampaignCapacity';
import { showToastMessage } from '../../../utils/AppUtils';
import { updateOffsiteCampaign } from '../../../redux/buzzbridge/actions';
import { updatingCampaignSelector } from '../../../redux/buzzbridge/selectors';
import { fetchCampaignHostCreationAccess } from '../../../redux/campaignHostSubscription/actions';
import { campaignHostCreationAccessSelector } from '../../../redux/campaignHostSubscription/selectors';
import {
    clampCreatorsToPlanLimit,
    formatCreationAccessMaxCreatorsLabel,
    getCreationAccessMaxCreators,
    isOverCreatorPlanLimit,
} from '../../../utils/campaignHostCreationEntitlements';
import { RichTextEditor } from '../../../components/Common/RichTextEditor';
import { CustomDatePicker } from '../../../components/DatePicker/CustomDatePicker';
import CampaignFeatureImagePicker from './CampaignFeatureImagePicker';
import { RichMediaShareSizeHint } from '../../../components/Common/RichMediaShareSizeHint';
import { ClippingCampaignToggle } from '../ClippingCampaignToggle';
import { PremiumCampaignToggle, type PremiumRequirement } from '../PremiumCampaignToggle';
import { AppInstallCampaignToggle } from '../AppInstallCampaignToggle';
import type { IAppInstall } from '../../Dashboard/offsiteCampaignCreator.reducer';
import {
    datePickerSx,
    parseBudgetInput,
    NICHE_OPTIONS_ALPHABETICAL,
    NICHE_MAIN_ALPHABETICAL,
    getSubcategoriesForMain,
    MAIN_CATEGORY_QUICK_PICK,
    serializeCampaignLocation,
    campaignLocationToForm,
    isValidClippingAssetUrl,
} from '../ugcCampaignFormHelpers';
import { PlacesAutocompleteField } from '../../../components/Common/PlacesAutocompleteField';
import type { PlacesSuggestionPalette } from '../../../components/Common/PlacesAutocompleteField';
import { useGoogleMapsLoader } from '../../../hook/useMapLoader';
import type { ILocation } from '../../../types/global/helper';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_OUTLINE_VARIANT,
} from '../brandDashboardTokens';
import { WorldwideCampaignBlock } from '../WorldwideCampaignBlock';

interface Props {
    open: boolean;
    onClose: () => void;
    campaign: ICampaign;
    onSaved: () => void;
}

/* ── Shared label ──────────────────────────────────── */

function FieldLabel({ children }: { children: React.ReactNode }) {
    return (
        <Typography
            sx={{
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#fff',
                mb: 1.5,
            }}>
            {children}
        </Typography>
    );
}

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
        borderRadius: '12px',
        color: '#fff',
        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.22)' },
        '&.Mui-focused fieldset': { borderColor: BRAND_LUXE_PRIMARY, borderWidth: 1 },
    },
    '& .MuiInputBase-input': { color: '#fff' },
    '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
};

/* ── Component ─────────────────────────────────────── */

function serializeEditableAppInstall(ai?: IAppInstall | null) {
    const v = ai ?? { enabled: false };
    return JSON.stringify({
        enabled: Boolean(v.enabled),
        kind: v.kind === 'web_visit' ? 'web_visit' : 'app_install',
        app_display_name: String(v.app_display_name || ''),
        app_icon_url: String(v.app_icon_url || ''),
        android_package: String(v.android_package || ''),
        android_store_url: String(v.android_store_url || ''),
        ios_store_url: String(v.ios_store_url || ''),
        web_fallback_url: String(v.web_fallback_url || ''),
        auto_reload_enabled: Boolean(v.auto_reload_enabled),
        payment_method_id: String(v.payment_method_id || ''),
    });
}

export default function CampaignEditModal({ open, onClose, campaign, onSaved }: Props) {
    const dispatch = useDispatch();
    const updating = useSelector(updatingCampaignSelector);
    const creationAccess = useSelector(campaignHostCreationAccessSelector);
    const maxCreators = getCreationAccessMaxCreators(creationAccess);
    const creatorCapLabel = formatCreationAccessMaxCreatorsLabel(creationAccess);
    const { isLoaded: isMapsLoaded } = useGoogleMapsLoader();
    const buzzBridgeConfig = useSelector(buzzBridgeConfigSelector);
    const canUseOauthCampaignModes = areAllPlatformAutoLinksEnabled(buzzBridgeConfig);
    const disabledAutoLinkLabel = formatDisabledAutoLinkPlatforms(buzzBridgeConfig);
    const showPremiumControls = canUseOauthCampaignModes || Boolean(campaign.premium);

    useEffect(() => {
        if (open) dispatch(fetchCampaignHostCreationAccess());
    }, [open, dispatch]);

    const locationSuggestionPalette: PlacesSuggestionPalette = {
        rowInactiveBg: BRAND_SURFACE_CONTAINER_HIGHEST,
        rowInactiveColor: 'rgba(255,255,255,0.9)',
        rowActiveBg: BRAND_LUXE_PRIMARY,
        rowActiveColor: '#392700',
        loadingBg: BRAND_SURFACE_CONTAINER_HIGHEST,
        loadingColor: 'rgba(255,255,255,0.9)',
        listBorder: `1px solid ${BRAND_OUTLINE_VARIANT}`,
    };
    const placesFieldSx = {
        '& .MuiOutlinedInput-root': {
            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
            borderRadius: 2,
            color: '#fff',
            '& fieldset': { border: 'none' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: 'none' },
            '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(236,170,0,0.35)' },
        },
        '& .MuiInputLabel-root': {
            color: 'rgba(255,255,255,0.65)',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
        },
        '& .MuiOutlinedInput-input': { color: '#fff' },
        '& .MuiOutlinedInput-input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
    };

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState<ILocation>(campaignLocationToForm());
    const [budgetInput, setBudgetInput] = useState('');
    const [participantsCount, setParticipantsCount] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [deliveryDate, setDeliveryDate] = useState('');
    const [worldWide, setWorldWide] = useState(false);
    const [isClipping, setIsClipping] = useState(false);
    const [clippingAssetUrl, setClippingAssetUrl] = useState('');
    const [premium, setPremium] = useState(false);
    const [premiumRequirements, setPremiumRequirements] = useState<PremiumRequirement[]>([]);
    const [category, setCategory] = useState('');
    const [subCategory, setSubCategory] = useState('');
    const subCategoryOptions = useMemo(() => getSubcategoriesForMain(category), [category]);
    const [featureImageFile, setFeatureImageFile] = useState<File | null>(null);
    const [featureImagePreview, setFeatureImagePreview] = useState<string | null>(null);
    const [appIconFile, setAppIconFile] = useState<File | null>(null);
    const [commission, setCommission] = useState<ICampaignCommissionConfig>(emptyCommissionConfig('contest'));
    const [influencerReadme, setInfluencerReadme] = useState('');
    const [appInstall, setAppInstall] = useState<IAppInstall>({ enabled: false });
    const [basePaymentInput, setBasePaymentInput] = useState('');

    const isCommission =
        campaign.campaign_type === 'commission' ||
        Boolean(campaign.commission?.target_type) ||
        campaign.commission?.rate != null;

    useEffect(() => {
        if (!open) return;
        setTitle(campaign.title || '');
        setDescription(campaign.description || '');
        setLocation(campaignLocationToForm(campaign.location));
        setBudgetInput(String(campaign.total_budget || ''));
        setParticipantsCount(() => {
            const max = getUgcCampaignMaxSlots(campaign);
            return max != null && max > 0 ? String(max) : '';
        });
        setStartDate(campaign.start_date || '');
        setEndDate(campaign.end_date || '');
        setDeliveryDate((campaign as any).delivery_date || '');
        setWorldWide(Boolean(campaign.world_wide));
        setIsClipping(campaign.is_clipping || false);
        setClippingAssetUrl(campaign.clipping_asset_url || '');
        setPremium(Boolean(campaign.premium));
        setPremiumRequirements(
            Array.isArray(campaign.premium_requirements)
                ? (campaign.premium_requirements.filter(
                      (r): r is PremiumRequirement => r === 'instagram_automation' || r === 'auto_upload',
                  ) as PremiumRequirement[])
                : [],
        );
        setCategory(campaign.category ?? NICHE_MAIN_ALPHABETICAL[0]);
        setSubCategory(campaign.sub_category ?? '');
        setFeatureImagePreview(campaign.feature_image || null);
        setFeatureImageFile(null);
        setAppIconFile(null);
        const contestId =
            typeof campaign.commission?.contest === 'object' && campaign.commission?.contest
                ? String((campaign.commission.contest as { _id?: string })._id || '')
                : String(campaign.commission?.contest || campaign.commission_contest?._id || '');
        const seedCommission = Boolean(
            campaign.campaign_type === 'commission' ||
                campaign.commission?.target_type ||
                campaign.commission?.rate != null ||
                contestId,
        );
        setCommission(
            seedCommission
                ? {
                      ...emptyCommissionConfig(
                          campaign.commission?.target_type === 'external' ? 'external' : 'contest',
                      ),
                      ...(campaign.commission || {}),
                      target_type: campaign.commission?.target_type === 'external' ? 'external' : 'contest',
                      contest: contestId,
                      requires_approval: campaign.commission?.requires_approval !== false,
                  }
                : emptyCommissionConfig('contest'),
        );
        setInfluencerReadme(String(campaign.influencer_readme || ''));
        const baseCents = (campaign as any).base_payment_per_creator_cents;
        setBasePaymentInput(baseCents != null && baseCents > 0 ? String(Number(baseCents) / 100) : '');
        const srcInstall = campaign.app_install;
        setAppInstall(
            srcInstall?.enabled
                ? {
                      enabled: true,
                      kind: srcInstall.kind === 'web_visit' ? 'web_visit' : 'app_install',
                      app_display_name: srcInstall.app_display_name || '',
                      app_icon_url: srcInstall.app_icon_url || '',
                      android_package: srcInstall.android_package || '',
                      android_store_url: srcInstall.android_store_url || '',
                      ios_store_url: srcInstall.ios_store_url || '',
                      web_fallback_url: srcInstall.web_fallback_url || '',
                      auto_reload_enabled: Boolean(srcInstall.auto_reload_enabled),
                      payment_method_id: srcInstall.payment_method_id || '',
                  }
                : { enabled: false },
        );
    }, [open, campaign]);

    const daysLeft = useMemo(() => {
        if (!endDate) return null;
        const diff = Math.ceil((new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        return diff > 0 ? diff : 0;
    }, [endDate]);

    const isRunning = campaign.status === 'published';
    const hasExistingDeliveryDate = Boolean((campaign as any).delivery_date);
    const deliveryDateLocked = isRunning && hasExistingDeliveryDate;

    const minDate = dayjs().add(1, 'day').toISOString();

    const handleSave = () => {
        if (!isCommission && premium && premiumRequirements.length === 0) {
            showToastMessage('Select at least one premium requirement, or turn premium off.', 'error');
            return;
        }

        const body: Record<string, any> = {};

        if (title !== (campaign.title || '')) body.title = title;
        if (description !== (campaign.description || '')) body.description = description;

        const budget = parseBudgetInput(budgetInput);
        if ((!isCommission || appInstall.enabled) && budget && budget !== campaign.total_budget) {
            body.total_budget = budget;
            body.available_budget = budget;
        }

        const rawCount = parseInt(participantsCount, 10) || 0;
        if (isOverCreatorPlanLimit(rawCount, maxCreators)) {
            showToastMessage(creatorCapLabel || 'Too many creators for your plan.', 'error');
            return;
        }
        const count = clampCreatorsToPlanLimit(rawCount || 1, maxCreators);
        const baselineMax = getUgcCampaignMaxSlots(campaign) ?? 0;
        if (count !== baselineMax) {
            body.participants_count = count || undefined;
            body.total_participants_count = count || undefined;
        }

        const nextLocation = serializeCampaignLocation(location);
        const origLocation = serializeCampaignLocation(campaign.location);
        if (JSON.stringify(nextLocation ?? null) !== JSON.stringify(origLocation ?? null)) {
            body.location = nextLocation ?? null;
        }

        // app_install — include when toggled or config changed
        if (serializeEditableAppInstall(appInstall) !== serializeEditableAppInstall(campaign.app_install)) {
            if (
                appInstall?.enabled &&
                appInstall.auto_reload_enabled &&
                !String(appInstall.payment_method_id || '').trim()
            ) {
                showToastMessage('Select a card on file to enable auto-reload, or turn auto-reload off.', 'error');
                return;
            }
            body.app_install = appInstall;
        }

        if (appIconFile) {
            body.rawAppIconFile = appIconFile;
            if (!body.app_install) body.app_install = appInstall;
        }

        if (!isCommission) {
            const origIsClipping = campaign.is_clipping || false;
            const trimmedClippingUrl = clippingAssetUrl.trim();
            const origClippingUrl = (campaign.clipping_asset_url || '').trim();
            if (isClipping !== origIsClipping) body.is_clipping = isClipping;
            if (isClipping) {
                if (trimmedClippingUrl !== origClippingUrl) body.clipping_asset_url = trimmedClippingUrl;
            } else if (origClippingUrl || origIsClipping) {
                body.clipping_asset_url = null;
            }

            if (!canUseOauthCampaignModes && premium && !campaign.premium) {
                showToastMessage(
                    `Premium requires Instagram, TikTok, and YouTube auto-link. Turn on auto-integration for ${
                        disabledAutoLinkLabel || 'all platforms'
                    } in global config.`,
                    'error',
                );
                return;
            }

            const origPremium = Boolean(campaign.premium);
            const origReqs = Array.isArray(campaign.premium_requirements)
                ? [...campaign.premium_requirements].sort()
                : [];
            const nextReqs = premium ? [...premiumRequirements].sort() : [];
            if (!origPremium && premium) {
                if (!nextReqs.length) {
                    showToastMessage('Select at least one premium requirement before saving.', 'error');
                    return;
                }
                body.premium = true;
                body.premium_requirements = premiumRequirements;
            } else if (origPremium) {
                // Locked — never send a turn-off; keep stored requirements.
            } else if (premium !== origPremium || JSON.stringify(nextReqs) !== JSON.stringify(origReqs)) {
                body.premium = premium;
                body.premium_requirements = premium ? premiumRequirements : [];
            }
        } else {
            const commissionPayload = sanitizeCommissionForPayload(commission);
            if (!commissionPayload) {
                showToastMessage(
                    commission.target_type === 'external'
                        ? 'Add product name and a valid commission rate.'
                        : 'Select a contest and set a valid commission rate before saving.',
                    'error',
                );
                return;
            }
            // Always persist type + commission together so PATCH validation and linking stay in sync.
            body.campaign_type = 'commission';
            const existingProgram =
                typeof campaign.commission?.program === 'object' && campaign.commission?.program
                    ? (campaign.commission.program as { _id?: string })._id
                    : campaign.commission?.program;
            body.commission = {
                ...commissionPayload,
                ...(existingProgram ? { program: existingProgram } : {}),
            };
            const nextReadme = influencerReadme.trim();
            const origReadme = String(campaign.influencer_readme || '').trim();
            if (nextReadme !== origReadme) body.influencer_readme = nextReadme || undefined;
        }

        if (startDate !== (campaign.start_date || '')) body.start_date = startDate || undefined;
        if (endDate !== (campaign.end_date || '')) body.end_date = endDate || undefined;

        if (!deliveryDateLocked && deliveryDate !== ((campaign as any).delivery_date || '')) {
            body.delivery_date = deliveryDate || undefined;
        }
        if (isCommission || Boolean(appInstall?.enabled)) {
            const origBase = (campaign as any).base_payment_per_creator_cents ?? null;
            const parsedBase = basePaymentInput.trim() !== '' ? Math.round(parseFloat(basePaymentInput) * 100) : null;
            if (parsedBase !== origBase) body.base_payment_per_creator_cents = parsedBase;
        }

        if (worldWide !== Boolean(campaign.world_wide)) body.world_wide = worldWide;

        const origCategory = campaign.category ?? NICHE_MAIN_ALPHABETICAL[0];
        const origSub = campaign.sub_category ?? '';
        if (category !== origCategory) body.category = category;
        if (subCategory !== origSub) body.sub_category = subCategory || undefined;

        if (featureImageFile) {
            body.rawFeatureImage = featureImageFile;
        } else if (!featureImagePreview && campaign.feature_image) {
            body.feature_image = '';
        }

        if (Object.keys(body).length === 0) {
            onSaved();
            return;
        }

        dispatch(updateOffsiteCampaign({ campaignId: campaign._id, body }));
        onSaved();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    borderRadius: { xs: 0, sm: '16px' },
                    border: { xs: 'none', sm: `1px solid rgba(72,72,71,0.1)` },
                    boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                    overflow: 'hidden',
                    m: { xs: 0, sm: 3 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    width: { xs: '100%', sm: undefined },
                    height: { xs: '100%', sm: undefined },
                },
            }}
            sx={{
                '& .MuiBackdrop-root': { bgcolor: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)' },
            }}>
            {/* ── Header ──────────────────────────────────── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: { xs: 3, sm: 4 },
                    py: 2.5,
                    bgcolor: BRAND_SURFACE_CONTAINER,
                    borderBottom: `1px solid rgba(72,72,71,0.1)`,
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: { xs: 18, sm: 20 },
                        color: '#fff',
                        letterSpacing: '-0.01em',
                    }}>
                    Edit Campaign Details
                </Typography>
                <IconButton
                    onClick={onClose}
                    sx={{
                        color: 'rgba(255,255,255,0.4)',
                        '&:hover': { color: BRAND_LUXE_PRIMARY },
                        transition: 'color 0.2s',
                    }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* ── Form body ───────────────────────────────── */}
            <Box
                sx={{
                    px: { xs: 3, sm: 4 },
                    py: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                    maxHeight: { xs: 'calc(100vh - 160px)', sm: '62vh' },
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': { width: 4 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 },
                }}>
                {/* Feature Image — top of edit modal */}
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, mb: 2 }}>
                    <FieldLabel>
                        Feature Image
                        <RichMediaShareSizeHint inline sx={{ color: 'rgba(255,255,255,0.45)' }} />
                    </FieldLabel>
                    <Typography
                        sx={{
                            fontSize: 13,
                            color: 'rgba(255,255,255,0.55)',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            lineHeight: 1.5,
                            mb: 1.5,
                        }}>
                        Place a nice image here that best describes your campaign.
                    </Typography>
                    <CampaignFeatureImagePicker
                        preview={featureImagePreview}
                        onSelect={(file, preview) => {
                            setFeatureImageFile(file);
                            setFeatureImagePreview(preview);
                        }}
                        onClear={() => {
                            setFeatureImageFile(null);
                            setFeatureImagePreview(null);
                        }}
                    />
                </Box>

                {/* Title */}
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, mb: 2 }}>
                    <FieldLabel>Campaign Title</FieldLabel>
                    <TextField
                        fullWidth
                        placeholder="Enter campaign title"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        sx={{
                            ...fieldSx,
                            '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], py: 0.5 },
                            '& .MuiInputBase-input': { fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff' },
                        }}
                    />
                </Box>

                {/* Description */}
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, mb: 2 }}>
                    <FieldLabel>Campaign Brief</FieldLabel>
                    <RichTextEditor value={description} onChange={setDescription} shellVariant="brandDashboard" />
                </Box>

                {/* Location */}
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, mb: 2 }}>
                    {isMapsLoaded ? (
                        <PlacesAutocompleteField
                            label="Location"
                            placeholder="London, UK — start typing, then pick a suggestion"
                            value={location}
                            onChange={loc => {
                                const single = Array.isArray(loc) ? loc[0] : loc;
                                setLocation(single ?? campaignLocationToForm());
                            }}
                            textFieldSx={placesFieldSx}
                            suggestionPalette={locationSuggestionPalette}
                        />
                    ) : (
                        <>
                            <FieldLabel>Location</FieldLabel>
                            <TextField
                                fullWidth
                                placeholder="London, UK"
                                value={location.address || ''}
                                onChange={e => {
                                    const value = e.target.value;
                                    setLocation({
                                        type: 'Point',
                                        coordinates: [],
                                        address: value,
                                        description: value,
                                        short_name: value.split(',')[0]?.trim() || '',
                                    });
                                }}
                                sx={fieldSx}
                            />
                        </>
                    )}
                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', mt: 1.5 }}>
                        Where you'd ideally like your creators to be located. We'll prioritize showing your campaign to
                        creators from here. This will default to your current location if left empty.
                    </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <WorldwideCampaignBlock checked={worldWide} onChange={setWorldWide} />
                </Box>

                {isCommission ? (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <AppInstallCampaignToggle
                                key={`app-install-edit-${campaign._id}-commission`}
                                value={appInstall}
                                onChange={setAppInstall}
                                onAppIconFile={setAppIconFile}
                                influencerTier={campaign.influencer_tier || ''}
                            />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <CommissionCampaignFields
                                value={commission}
                                influencerReadme={influencerReadme}
                                onChange={setCommission}
                                onReadmeChange={setInfluencerReadme}
                                brandSurface={BRAND_SURFACE_CONTAINER_LOW}
                                linkedContestLabel={campaign.commission_contest?.title}
                            />
                        </Box>
                    </>
                ) : null}

                {!isCommission ? (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <ClippingCampaignToggle
                                checked={isClipping}
                                assetUrl={clippingAssetUrl}
                                onCheckedChange={v => {
                                    setIsClipping(v);
                                    if (!v) setClippingAssetUrl('');
                                }}
                                onAssetUrlChange={setClippingAssetUrl}
                                fieldSx={fieldSx}
                            />
                        </Box>

                        {showPremiumControls ? (
                            <Box sx={{ mb: 2 }}>
                                <PremiumCampaignToggle
                                    premium={premium}
                                    requirements={premiumRequirements}
                                    onPremiumChange={v => {
                                        if (!canUseOauthCampaignModes && v && !campaign.premium) return;
                                        setPremium(v);
                                    }}
                                    onRequirementsChange={setPremiumRequirements}
                                    locked={Boolean(campaign.premium) || !canUseOauthCampaignModes}
                                />
                            </Box>
                        ) : null}
                    </>
                ) : null}

                {/* Content category */}
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, mb: 2 }}>
                    <FieldLabel>Content category</FieldLabel>
                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', mb: 2, lineHeight: 1.55 }}>
                        This helps us match your campaign to the right creators. Pick the closest vertical, then get as
                        niche as you can with the sub-category. Shortcuts are the first {MAIN_CATEGORY_QUICK_PICK} for
                        quick access.
                    </Typography>
                    <FieldLabel>Search all categories</FieldLabel>
                    <Autocomplete
                        options={NICHE_OPTIONS_ALPHABETICAL}
                        getOptionLabel={o => o.label}
                        value={NICHE_OPTIONS_ALPHABETICAL.find(o => o.key === category) ?? null}
                        onChange={(_, opt) => {
                            if (opt) {
                                setCategory(opt.key);
                                setSubCategory('');
                            }
                        }}
                        isOptionEqualToValue={(a, b) => a.key === b.key}
                        clearOnBlur={false}
                        selectOnFocus
                        handleHomeEndKeys
                        sx={{
                            mb: 2,
                            '& .MuiInputBase-input': { fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 15, color: '#fff' },
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    color: '#fff',
                                    '& .MuiAutocomplete-option.Mui-focused': { bgcolor: 'rgba(255,201,101,0.12)' },
                                },
                            },
                        }}
                        renderInput={params => {
                            const sel = NICHE_OPTIONS_ALPHABETICAL.find(o => o.key === category);
                            const SelIcon = sel?.Icon;
                            return (
                                <TextField
                                    {...params}
                                    placeholder="Type to search…"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: SelIcon ? (
                                            <>
                                                <InputAdornment position="start" sx={{ ml: 0.5 }}>
                                                    <SelIcon sx={{ fontSize: 22, color: BRAND_LUXE_PRIMARY }} />
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ) : (
                                            params.InputProps.startAdornment
                                        ),
                                    }}
                                    sx={{
                                        ...fieldSx,
                                        '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], py: 0.5 },
                                    }}
                                />
                            );
                        }}
                    />
                    <FieldLabel>Popular shortcuts</FieldLabel>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
                        {NICHE_OPTIONS_ALPHABETICAL.slice(0, MAIN_CATEGORY_QUICK_PICK).map(({ key, label, Icon }) => {
                            const active = category === key;
                            return (
                                <Button
                                    key={key}
                                    size="small"
                                    onClick={() => {
                                        setCategory(key);
                                        setSubCategory('');
                                    }}
                                    startIcon={<Icon sx={{ fontSize: 20 }} />}
                                    sx={{
                                        borderRadius: 999,
                                        px: 2,
                                        py: 1,
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 13,
                                        border: 'none',
                                        bgcolor: active ? BRAND_LUXE_PRIMARY : BRAND_SURFACE_CONTAINER_HIGHEST,
                                        color: active ? '#5f4200' : '#fff',
                                        '&:hover': {
                                            bgcolor: active ? BRAND_LUXE_PRIMARY : '#2c2c2c',
                                        },
                                    }}>
                                    {label}
                                </Button>
                            );
                        })}
                    </Box>
                    <FieldLabel>Sub-category</FieldLabel>
                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', mb: 1 }}>
                        {category ? `Options for ${category} — search to filter.` : 'Select a main category first.'}
                    </Typography>
                    <Autocomplete
                        disabled={!category}
                        value={subCategory || null}
                        onChange={(_, value) => setSubCategory(value || '')}
                        options={subCategoryOptions}
                        isOptionEqualToValue={(a, b) => a === b}
                        sx={{ mt: 1 }}
                        slotProps={{
                            paper: {
                                sx: {
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    color: '#fff',
                                    '& .MuiAutocomplete-option.Mui-focused': { bgcolor: 'rgba(255,201,101,0.12)' },
                                },
                            },
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                placeholder="Search or select sub-category…"
                                sx={{
                                    ...fieldSx,
                                    '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], py: 0.5 },
                                }}
                            />
                        )}
                    />
                </Box>

                {/* Budget + Participants */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: isCommission && !appInstall.enabled ? '1fr' : '1fr 1fr',
                        },
                        gap: 2,
                        mb: 2,
                    }}>
                    {!isCommission || appInstall.enabled ? (
                        <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                            <FieldLabel>Total Budget</FieldLabel>
                            <TextField
                                fullWidth
                                type="text"
                                inputMode="decimal"
                                placeholder="12,500"
                                value={budgetInput}
                                onChange={e => setBudgetInput(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography
                                                sx={{ color: BRAND_LUXE_PRIMARY, fontWeight: 700, fontSize: 20 }}>
                                                $
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    ...fieldSx,
                                    '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], pl: 1 },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 20,
                                        color: '#fff',
                                    },
                                }}
                            />
                        </Box>
                    ) : null}
                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                        <FieldLabel>{isCommission ? 'Creator seats' : 'Max Participants'}</FieldLabel>
                        {creatorCapLabel ? (
                            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mb: 1.5, lineHeight: 1.5 }}>
                                {creatorCapLabel}
                            </Typography>
                        ) : null}
                        <TextField
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, ...(maxCreators != null ? { max: maxCreators } : {}) }}
                            placeholder={maxCreators != null ? `1–${maxCreators}` : 'e.g. 10'}
                            value={participantsCount}
                            onChange={e => {
                                const n = parseInt(e.target.value, 10);
                                if (!Number.isFinite(n)) {
                                    setParticipantsCount(e.target.value);
                                    return;
                                }
                                setParticipantsCount(String(clampCreatorsToPlanLimit(n, maxCreators)));
                            }}
                            error={isOverCreatorPlanLimit(parseInt(participantsCount, 10) || 0, maxCreators)}
                            sx={fieldSx}
                        />
                    </Box>
                </Box>

                {(isCommission || appInstall.enabled) ? (
                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, mb: 2 }}>
                        <FieldLabel>Base pay per creator</FieldLabel>
                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mb: 1.5, lineHeight: 1.5 }}>
                            {isCommission && !appInstall.enabled
                                ? 'Each creator receives this flat amount upon approval. Paid from your commission wallet, separate from the display pool.'
                                : 'Each creator receives this amount upon approval. Leave blank to distribute from the total budget.'}
                        </Typography>
                        <TextField
                            fullWidth
                            type="text"
                            inputMode="decimal"
                            size="small"
                            placeholder="0.00"
                            value={basePaymentInput}
                            onChange={e => {
                                const raw = e.target.value;
                                if (raw === '' || /^[\d.]*$/.test(raw)) setBasePaymentInput(raw);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography sx={{ color: BRAND_LUXE_PRIMARY, fontWeight: 700, fontSize: 16 }}>
                                            $
                                        </Typography>
                                    </InputAdornment>
                                ),
                            }}
                            sx={fieldSx}
                        />
                    </Box>
                ) : null}

                {/* Campaign Timeline */}
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, mb: 2 }}>
                    <FieldLabel>Campaign Timeline</FieldLabel>
                    <Box
                        sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: 'rgba(255,255,255,0.4)',
                                    mb: 0.5,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    letterSpacing: '0.1em',
                                }}>
                                START
                            </Typography>
                            <CustomDatePicker
                                value={startDate || undefined}
                                onChange={val => setStartDate(val || '')}
                                minDate={minDate}
                                withTime={false}
                                withTimezone={false}
                                sx={datePickerSx}
                            />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: deliveryDateLocked ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.4)',
                                    mb: 0.5,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    letterSpacing: '0.1em',
                                }}>
                                DELIVERY
                            </Typography>
                            <CustomDatePicker
                                value={deliveryDate || undefined}
                                onChange={val => setDeliveryDate(val || '')}
                                minDate={
                                    startDate && dayjs(startDate).isValid()
                                        ? dayjs(startDate).add(7, 'day').toISOString()
                                        : dayjs().add(8, 'day').toISOString()
                                }
                                maxDate={
                                    endDate && dayjs(endDate).isValid()
                                        ? dayjs(endDate).subtract(1, 'day').toISOString()
                                        : undefined
                                }
                                withTime={false}
                                withTimezone={false}
                                disabled={deliveryDateLocked}
                                sx={datePickerSx}
                            />
                            {deliveryDateLocked && (
                                <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', mt: 0.75 }}>
                                    Cannot be changed once set on a running campaign.
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: 'rgba(255,255,255,0.4)',
                                    mb: 0.5,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    letterSpacing: '0.1em',
                                }}>
                                END
                            </Typography>
                            <CustomDatePicker
                                value={endDate || undefined}
                                onChange={val => setEndDate(val || '')}
                                minDate={
                                    deliveryDate && dayjs(deliveryDate).isValid()
                                        ? dayjs(deliveryDate).add(1, 'day').toISOString()
                                        : startDate || minDate
                                }
                                withTime={false}
                                withTimezone={false}
                                sx={datePickerSx}
                            />
                        </Box>
                        {daysLeft !== null && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    px: 2,
                                    borderRadius: '12px',
                                    border: `1px solid rgba(72,72,71,0.1)`,
                                    flexShrink: 0,
                                    minWidth: 'fit-content',
                                    alignSelf: 'flex-end',
                                    height: 48,
                                }}>
                                <TimerOutlinedIcon sx={{ fontSize: 18, color: BRAND_LUXE_SECONDARY }} />
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 13,
                                        color: 'rgba(255,255,255,0.6)',
                                        whiteSpace: 'nowrap',
                                    }}>
                                    {daysLeft} DAYS LEFT
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* App / Web Install — legacy standard/direct-hire CPA campaigns */}
                {!isCommission && (
                    <Box sx={{ mb: 2 }}>
                        <AppInstallCampaignToggle
                            key={`app-install-edit-${campaign._id}-standard`}
                            value={appInstall}
                            onChange={setAppInstall}
                            onAppIconFile={setAppIconFile}
                            influencerTier={campaign.influencer_tier || ''}
                        />
                    </Box>
                )}
            </Box>

            {/* ── Actions ─────────────────────────────────── */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row-reverse' },
                    gap: 2,
                    px: { xs: 3, sm: 4 },
                    py: 3,
                    bgcolor: `${BRAND_SURFACE_CONTAINER}80`,
                    borderTop: `1px solid rgba(72,72,71,0.1)`,
                }}>
                <Button
                    onClick={handleSave}
                    disabled={
                        !title.trim() ||
                        updating ||
                        (!isCommission && isClipping && !isValidClippingAssetUrl(clippingAssetUrl))
                    }
                    sx={{
                        flex: { xs: 1, sm: 'none' },
                        px: 5,
                        py: 1.8,
                        borderRadius: 999,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 14,
                        letterSpacing: '-0.01em',
                        background: `linear-gradient(135deg, ${BRAND_LUXE_PRIMARY}, #feb700)`,
                        color: '#392700',
                        boxShadow: '0 8px 24px rgba(255,193,7,0.12)',
                        transition: 'all 0.2s',
                        '&:hover': { transform: 'scale(1.02)', boxShadow: '0 12px 32px rgba(255,193,7,0.2)' },
                        '&:active': { transform: 'scale(0.97)' },
                        ':disabled': {
                            background: 'rgba(255,255,255,0.06)',
                            color: 'rgba(255,255,255,0.25)',
                            boxShadow: 'none',
                        },
                    }}>
                    {updating && <CircularProgress size={20} sx={{ color: '#392700', mr: 1 }} />}
                    {updating ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                    onClick={onClose}
                    sx={{
                        flex: { xs: 1, sm: 'none' },
                        px: 5,
                        py: 1.8,
                        borderRadius: 999,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 14,
                        border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                        color: 'rgba(255,255,255,0.6)',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST },
                    }}>
                    Cancel
                </Button>
            </Box>
        </Dialog>
    );
}
